/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';

export const ORGANIZATIONS = [
  'm42-platform',
  'm42-games',
  'm42-sandbox',
] as const;
export type Organization = (typeof ORGANIZATIONS)[number];

export interface ServiceRepoValue {
  repository: string;
  organization: Organization;
  exists: boolean;
  entityRef: string | null;
  owner: string;
  system: string;
  description: string;
  rootPackage: string;
}

const REPOSITORY_PATTERN = /^[A-Za-z0-9._-]+$/;
// New names only: GHA-CodeArtifact-Publish-maven-<repo> prefixes 31 chars against
// IAM's 64-char role name limit; existing repos import at whatever length they have.
const REPOSITORY_MAX_LENGTH = 33;
const AUTO_DISCOVERED_ANNOTATION = 'm42.dev/auto-discovered';
const NATURE_PREFIXES = ['svc', 'lib', 'web', 'app', 'base', 'infra'];
const ORG_ORDER: Record<Organization, number> = {
  'm42-platform': 0,
  'm42-games': 1,
  'm42-sandbox': 2,
};

const EMPTY_VALUE: ServiceRepoValue = {
  repository: '',
  organization: 'm42-platform',
  exists: false,
  entityRef: null,
  owner: '',
  system: '',
  description: '',
  rootPackage: '',
};

/** svc-game-stats → m42.svc.gamestats (m42.<nature>.<name> without separators) */
export function suggestRootPackage(repository: string): string {
  const segments = repository
    .toLowerCase()
    .split(/[-_.]+/)
    .filter(Boolean);
  if (segments.length === 0) return '';
  const hasNature = NATURE_PREFIXES.includes(segments[0]);
  const nature = hasNature ? segments[0] : 'svc';
  const name = (hasNature ? segments.slice(1) : segments)
    .join('')
    .replace(/[^a-z0-9]/g, '');
  return name ? `m42.${nature}.${name}` : '';
}

/** m42-online → group:default/m42-online (full ref preserved as-is) */
export function normalizeOwnerRef(owner: string): string {
  if (!owner) return '';
  try {
    return stringifyEntityRef(
      parseEntityRef(owner, { defaultKind: 'group', defaultNamespace: 'default' }),
    );
  } catch {
    return owner;
  }
}

function toOrganization(entity: Entity): Organization {
  const namespace = entity.metadata.namespace ?? 'default';
  // Default-namespace Components are all m42-platform by provider design.
  if (namespace === 'default') return 'm42-platform';
  if ((ORGANIZATIONS as readonly string[]).includes(namespace)) {
    return namespace as Organization;
  }
  const tagged = entity.metadata.tags?.find(tag =>
    (ORGANIZATIONS as readonly string[]).includes(tag),
  );
  return (tagged as Organization | undefined) ?? 'm42-platform';
}

export interface FleetOption {
  name: string;
  organization: Organization;
  entityRef: string;
  owner: string;
  system: string;
  description: string;
  autoDiscovered: boolean;
}

export function toFleetOption(entity: Entity): FleetOption {
  const spec = (entity.spec ?? {}) as { owner?: unknown; system?: unknown };
  return {
    name: entity.metadata.name,
    organization: toOrganization(entity),
    entityRef: stringifyEntityRef(entity),
    owner: normalizeOwnerRef(typeof spec.owner === 'string' ? spec.owner : ''),
    system: typeof spec.system === 'string' ? spec.system : '',
    description: entity.metadata.description ?? '',
    autoDiscovered:
      entity.metadata.annotations?.[AUTO_DISCOVERED_ANNOTATION] === 'true',
  };
}

interface FleetState {
  options: FleetOption[];
  loading: boolean;
  loadError?: string;
}

/** One fetch on mount — the fleet is ~300 entities, filtered client-side. */
function useFleetOptions(): FleetState {
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<FleetState>({ options: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Component', 'metadata.tags': [...ORGANIZATIONS] },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.description',
          'metadata.annotations',
          'metadata.tags',
          'spec.owner',
          'spec.system',
          'spec.type',
        ],
      })
      .then(({ items }) => {
        if (cancelled) return;
        const options = items
          .map(toFleetOption)
          .sort(
            (a, b) =>
              ORG_ORDER[a.organization] - ORG_ORDER[b.organization] ||
              a.name.localeCompare(b.name),
          );
        setState({ options, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({
          options: [],
          loading: false,
          loadError:
            'Failed to load repositories from the catalog — you can still type a repository name.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return state;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  subFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    cursor: 'default',
  },
}));

export const ServiceRepoAutocomplete = ({
  onChange,
  formData,
  rawErrors,
  schema,
}: FieldExtensionComponentProps<ServiceRepoValue>) => {
  const classes = useStyles();
  const { options, loading, loadError } = useFleetOptions();

  // rootPackage is JVM-only: hidden unless the template schema declares it,
  // and the autofill suggestion is skipped when hidden (PLAT-929).
  const rootPackageDeclared = Boolean(schema?.properties?.rootPackage);
  const rootPackageRequired =
    Array.isArray(schema?.required) && schema.required.includes('rootPackage');

  const value = useMemo<ServiceRepoValue>(
    () => ({ ...EMPTY_VALUE, ...formData }),
    [formData],
  );

  const selectedOption = useMemo(
    () =>
      value.entityRef
        ? options.find(o => o.entityRef === value.entityRef)
        : undefined,
    [options, value.entityRef],
  );

  const applyOption = useCallback(
    (option: FleetOption) => {
      onChange({
        repository: option.name,
        organization: option.organization,
        exists: true,
        entityRef: option.entityRef,
        owner: option.owner,
        system: option.system,
        description: option.description,
        rootPackage: rootPackageDeclared ? suggestRootPackage(option.name) : '',
      });
    },
    [onChange, rootPackageDeclared],
  );

  const applyRepositoryText = useCallback(
    (text: string) => {
      const match = options.find(
        o => o.name.toLowerCase() === text.toLowerCase(),
      );
      if (match) {
        applyOption(match);
        return;
      }
      // Leaving an autofilled selection wipes the derived fields; otherwise
      // keep whatever the user already typed into the sub-fields.
      const base = value.exists ? EMPTY_VALUE : value;
      onChange({ ...base, repository: text, exists: false, entityRef: null });
    },
    [options, value, applyOption, onChange],
  );

  // Reconcile text entered while the fleet fetch was still in flight: once
  // the options arrive, an exact (case-insensitive) match adopts the entity
  // instead of staying stuck as a bogus exists:false create.
  useEffect(() => {
    if (loading || value.exists || !value.repository) return;
    const match = options.find(
      o => o.name.toLowerCase() === value.repository.toLowerCase(),
    );
    if (match) applyOption(match);
  }, [loading, options, value, applyOption]);

  const setField = useCallback(
    <K extends keyof ServiceRepoValue>(
      key: K,
      fieldValue: ServiceRepoValue[K],
    ) => {
      onChange({ ...value, [key]: fieldValue });
    },
    [onChange, value],
  );

  const showAutoDiscoveredWarning =
    value.exists && (selectedOption?.autoDiscovered ?? false);
  const showCreateWarning =
    !loading && !value.exists && value.repository.trim().length > 0;

  return (
    <Box className={classes.root}>
      <Autocomplete<FleetOption, false, false, true>
        freeSolo
        options={options}
        loading={loading}
        loadingText="Loading repositories…"
        inputValue={value.repository}
        onInputChange={(_, text, reason) => {
          if (reason === 'input' || reason === 'clear') {
            applyRepositoryText(text);
          }
        }}
        onChange={(_, selected) => {
          if (typeof selected === 'string') {
            applyRepositoryText(selected);
          } else if (selected) {
            applyOption(selected);
          }
        }}
        getOptionLabel={(option: FleetOption | string) =>
          typeof option === 'string' ? option : option.name
        }
        groupBy={option => option.organization}
        renderOption={option => (
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            {option.description && (
              <Typography variant="caption" color="textSecondary">
                {option.description}
              </Typography>
            )}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Repository"
            required
            placeholder="Pick an onboarded repository or type a new name"
            error={rawErrors && rawErrors.length > 0}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <Tooltip title="Options come from the live Backstage catalog (all onboarded org repos)">
                    <InfoOutlinedIcon
                      fontSize="small"
                      className={classes.infoIcon}
                    />
                  </Tooltip>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {showAutoDiscoveredWarning && (
        <Alert severity="warning">
          Prefilled from an auto-discovered stub — owner/system are org
          defaults, verify before continuing
        </Alert>
      )}

      {showCreateWarning && (
        <Alert severity="warning">
          This repository does not exist yet — it will be CREATED and
          registered in .github-private as part of this run. Fill every field
          manually. If you created the repo in the last ~30 minutes it may
          simply not be ingested yet — picking it from the list once it
          appears avoids a duplicate-create attempt (the create step tolerates
          existing repos).
          {value.organization !== 'm42-platform' &&
            ' Note: automatic .github-private registration only covers m42-platform — register this repo manually.'}
        </Alert>
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      <Box className={classes.subFields}>
        <TextField
          select
          id="service-repo-organization"
          label="Organization"
          required
          disabled={value.exists}
          value={value.organization}
          onChange={e => setField('organization', e.target.value as Organization)}
          helperText={
            value.exists
              ? 'Derived from the catalog entity'
              : 'Where the repository will be created'
          }
        >
          {ORGANIZATIONS.map(org => (
            <MenuItem key={org} value={org}>
              {org}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="service-repo-owner"
          label="Owner"
          required
          value={value.owner}
          onChange={e => setField('owner', e.target.value)}
          onBlur={() => setField('owner', normalizeOwnerRef(value.owner))}
          helperText="Owning group, e.g. m42-online or group:default/m42-online"
        />
        <TextField
          id="service-repo-system"
          label="System"
          value={value.system}
          onChange={e => setField('system', e.target.value)}
          helperText="Optional"
        />
        {rootPackageDeclared && (
          <TextField
            id="service-repo-root-package"
            label="Root package"
            required={rootPackageRequired}
            value={value.rootPackage}
            placeholder="m42.svc.template"
            onChange={e => setField('rootPackage', e.target.value)}
          />
        )}
        <TextField
          id="service-repo-description"
          className={classes.fullWidth}
          label="Description"
          required
          multiline
          value={value.description}
          onChange={e => setField('description', e.target.value)}
        />
      </Box>

      {rawErrors && rawErrors.length > 0 && (
        <Typography color="error" variant="body2">
          {rawErrors.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export const serviceRepoAutocompleteValidation = (
  value: ServiceRepoValue,
  validation: { addError: (message: string) => void },
) => {
  if (!value) {
    validation.addError('Repository selection is required');
    return;
  }
  if (!value.repository || !REPOSITORY_PATTERN.test(value.repository)) {
    validation.addError(
      'Repository name must be letters, numbers, dots, underscores, or hyphens',
    );
  } else if (!value.exists && value.repository.length > REPOSITORY_MAX_LENGTH) {
    validation.addError(
      `New repository names are capped at ${REPOSITORY_MAX_LENGTH} characters (this one is ` +
        `${value.repository.length}). The cap is the CodeArtifact maven publisher role ` +
        'GHA-CodeArtifact-Publish-maven-<name>, which cannot exceed the 64-character IAM role ' +
        'name limit. A longer name can only get a publisher role by hand.',
    );
  }
  if (!(ORGANIZATIONS as readonly string[]).includes(value.organization)) {
    validation.addError('Organization is required');
  }
  // owner requiredness is schema-owned (like rootPackage): the spring template
  // requires it, the lib template does not consume it. The validator only owns
  // the format check when a value is present.
  if (value.owner && value.owner.trim().length > 0) {
    try {
      parseEntityRef(value.owner, {
        defaultKind: 'group',
        defaultNamespace: 'default',
      });
    } catch {
      validation.addError(
        'Owner must be an entity ref, e.g. group:default/m42-online',
      );
    }
  }
  if (!value.description || value.description.trim().length === 0) {
    validation.addError('Description is required');
  }
  // rootPackage requiredness is owned by each template's schema (required +
  // minLength) — ajv enforces it independently of this custom widget. The
  // spring template requires it; the lib template does not consume it.
};
